import { Link } from "react-router-dom";
import SEO, { getServiceSchema, getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, Home, Wrench, DollarSign, FileText, RotateCw } from "lucide-react";
import { motion } from "framer-motion";

const ManagementProcess = () => {
  const processSteps = [
    {
      icon: ClipboardCheck,
      title: "Property Onboarding",
      description: "Comprehensive property inspection, photography, and documentation. We create a detailed baseline report of your Kansas property.",
      timeline: "Week 1"
    },
    {
      icon: Home,
      title: "Tenant Placement",
      description: "Marketing, showing, screening, and lease signing. We find quality long-term tenants for your Overland Park or Leawood property.",
      timeline: "Weeks 2-3"
    },
    {
      icon: Wrench,
      title: "24/7 Maintenance",
      description: "Coordinated repairs, vetted contractors, and preventive maintenance. Tenants report issues online; we handle everything.",
      timeline: "Ongoing"
    },
    {
      icon: DollarSign,
      title: "Rent Collection",
      description: "Automated online payments, late fee enforcement, and collections. 99% collection rate with consistent cash flow.",
      timeline: "Monthly"
    },
    {
      icon: FileText,
      title: "Financial Reporting",
      description: "Monthly statements, 1099s, and real-time dashboard access. Complete transparency on income and expenses.",
      timeline: "Monthly"
    },
    {
      icon: RotateCw,
      title: "Lease Renewals",
      description: "Proactive renewal negotiations, market rent adjustments, and turnover management when needed.",
      timeline: "Annual"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Property Management Process | Rently Properties Kansas City"
        description="Our proven management process for Kansas City metro properties. From onboarding to rent collection and lease renewals, see how we manage rentals in Overland Park, Leawood, Lenexa & Olathe."
        canonical="/management-process"
        keywords="property management process, Kansas City rental management, tenant onboarding, rent collection process, maintenance coordination, Johnson County property management"
        schema={[
          getServiceSchema(
            "Full-Service Property Management",
            "End-to-end property management including onboarding, tenant placement, 24/7 maintenance, rent collection, and financial reporting for Johnson County properties."
          ),
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Management Process", path: "/management-process" }
          ])
        ]}
      />
      <Header />

      <main>
        <section className="py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Our <span className="text-primary">Management Process</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              From onboarding to ongoing management - here's exactly how we protect and maximize your Johnson County investment.
            </p>
          </div>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-6">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <Icon className="h-6 w-6" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-display text-xl font-semibold">
                                {step.title}
                              </h3>
                              <Badge variant="outline">{step.timeline}</Badge>
                            </div>
                            <p className="text-muted-foreground">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-12">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
              <Link to="/#contact">Start Managing Your Property</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ManagementProcess;
